// Phase 0 of transcript merge v2: a dev-only ring buffer that records
// field-edit events (key, before, after, timestamp) so we can answer the
// "what's the persisted value when a manager clears a text input?"
// question with real session data.
// See docs/planning/transcript_merge_v2.md §"Diagnostic logging (Phase 0)".
//
// Pure module: no React, no auth, no global side effects beyond the
// module-local ring buffer. Call sites are responsible for deciding
// when to record (e.g. only when dev mode is on).

const RING_CAPACITY = 256;

export type LeadFieldEditEntry = {
  ts: number;
  key: string;
  before: unknown;
  after: unknown;
  /**
   * Type of the `after` value as observed by JS at write time. Useful for
   * answering "is a cleared text input persisted as null, undefined, or
   * empty string?" without re-walking JSON.stringify of the before/after.
   */
  afterType: string;
};

let buffer: LeadFieldEditEntry[] = [];
const listeners = new Set<() => void>();

/** Append a single edit. Drops the oldest entry once capacity is reached. */
export function recordFieldEdit(entry: Omit<LeadFieldEditEntry, "ts" | "afterType">): void {
  const next: LeadFieldEditEntry = {
    ts: Date.now(),
    key: entry.key,
    before: entry.before,
    after: entry.after,
    afterType: describeType(entry.after),
  };
  buffer.push(next);
  if (buffer.length > RING_CAPACITY) {
    buffer = buffer.slice(buffer.length - RING_CAPACITY);
  }
  for (const cb of listeners) cb();
}

/** Snapshot of the ring buffer in oldest→newest order. */
export function getFieldEditLog(): readonly LeadFieldEditEntry[] {
  return buffer;
}

/** Wipe the buffer. Used by the dev viewer's "Clear" button. */
export function clearFieldEditLog(): void {
  buffer = [];
  for (const cb of listeners) cb();
}

/**
 * Subscribe to ring-buffer mutations. Returns an unsubscribe function.
 * Used by the dev viewer to re-render when new edits land.
 */
export function subscribeFieldEditLog(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Buffer capacity. Exported for tests + the dev viewer's UX copy. */
export const FIELD_EDIT_LOG_CAPACITY = RING_CAPACITY;

function describeType(v: unknown): string {
  if (v === null) return "null";
  if (Array.isArray(v)) return "array";
  return typeof v;
}
